/**
 * WEB-1114 — silent session renewal.
 *
 * The server has issued a rotating refresh token since day one
 * (POST /auth/refresh — see backend/src/auth/controllers/auth.controller.ts),
 * but the client never called it, so the access-token TTL could not be
 * shortened without bouncing everyone to /login on a fixed schedule.
 *
 * This module is deliberately dependency-injected and framework-free — no
 * axios, no localStorage, no antd — so the two hard requirements from the
 * ticket can be unit-tested directly instead of through HTTP mocking:
 *
 *  1. A race of concurrent 401s (many requests failing on the same expired
 *     access token at once) must trigger exactly ONE network refresh, with
 *     every caller sharing its outcome — not one refresh per request.
 *  2. A refresh token the server rejects — because it expired on its own,
 *     OR because the session was revoked server-side (WEB-1091:
 *     tokenVersion bump + refreshToken=null via UsersRepository.revokeSessions,
 *     which is what AuthService.refreshTokens checks) — must end the
 *     client-side session exactly once per failed refresh, not once per
 *     queued request, and must never resurrect the revoked session.
 *
 * client.ts wires the real HTTP call, localStorage, and a toast/logout
 * callback in as `TokenRefreshDeps`.
 */

export interface RefreshResult {
  readonly accessToken: string;
  readonly refreshToken: string;
}

export interface TokenRefreshDeps {
  /** Reads the currently stored refresh token, or null if there isn't one. */
  readonly getStoredRefreshToken: () => string | null;
  /** Calls POST /auth/refresh. Rejects on any non-2xx (expired/revoked/invalid). */
  readonly postRefresh: (refreshToken: string) => Promise<RefreshResult>;
  /** Called once, only on a successful refresh — persist the new pair. */
  readonly onRefreshed: (result: RefreshResult) => void;
  /**
   * Called once per failed refresh attempt (not once per queued caller) —
   * clear client-side session state and let the caller redirect to /login
   * with an explanation, rather than surfacing a raw technical error.
   */
  readonly onSessionExpired: () => void;
  /**
   * WEB-2613 — неудача, которая НЕ говорит о конце сессии: истёк предел
   * ожидания запроса обновления, оборвалась сеть, сервер временно недоступен.
   * Для неё onSessionExpired не зовётся — ждавшие получают отказ, человек
   * остаётся в кабинете, следующая попытка обновления идёт сразу (эпизод
   * закрывается как обычно). Не задан — любая неудача завершает сессию
   * (прежнее поведение WEB-1114).
   */
  readonly isTransientFailure?: (err: unknown) => boolean;
}

/**
 * WEB-1848 — who is allowed to END the session when a refresh fails.
 *
 * The 401 branch of the interceptor asks for a refresh because the session
 * itself may be over — there, a rejected refresh IS the verdict, and
 * onSessionExpired must fire (WEB-1114 §2, unchanged: that's the default).
 *
 * The 403 EMAIL_NOT_VERIFIED branch asks for a refresh only speculatively
 * ("maybe this token is stale and the email is already verified elsewhere").
 * Its failure says nothing about the session — GET /auth/me had just answered
 * 200 in the reported trace — so it must NOT tear the session down. Before
 * this flag it did: an unverified user opening an invite link got logged out
 * of a session that was alive.
 */
export interface RefreshAccessTokenOptions {
  /** Default true — a failed refresh ends the client-side session. */
  readonly endSessionOnFailure?: boolean;
}

export interface TokenRefreshCoordinator {
  /**
   * Returns the (possibly shared, in-flight) promise for the current refresh
   * attempt, starting a new one if none is in flight. Safe to call from
   * multiple concurrent 401 handlers — see module docstring §1.
   */
  readonly refreshAccessToken: (
    options?: RefreshAccessTokenOptions,
  ) => Promise<string>;
}

export function createTokenRefreshCoordinator(
  deps: TokenRefreshDeps,
): TokenRefreshCoordinator {
  let inFlight: Promise<string> | null = null;
  // WEB-1848 — per-episode, not per-caller: concurrent callers share ONE
  // network refresh (WEB-1114 §1), so they must also share ONE verdict. The
  // rule is "any caller that would end the session wins", i.e. a real 401
  // joining a speculative EMAIL_NOT_VERIFIED refresh still ends it.
  //
  // ГРАНИЦА ЭТОГО ПРАВИЛА (ревизия пачки 2, 02.09) — оно верно для попутчика,
  // пришедшего ДО ВЕРДИКТА. Есть узкое окно в один микротакт: catch ниже уже
  // прочитал флаг, а `finally` ещё не сбросил `inFlight`. Попутчик,
  // попавший ровно туда, поднимет флаг после чтения (поздно) и получит уже
  // отклонённый общий промис — его 401 эпизод не завершит.
  // Почему окно оставлено, а не закрыто:
  //   • оно самолечится — следующий запрос попадёт в новый эпизод, где 401
  //     сессию завершит штатно (тест «самолечение» ниже);
  //   • закрытие означает менять МОМЕНТ СМЕРТИ СЕССИИ, а это hardened-путь
  //     (auth): либо второй сетевой запрос на уже отвергнутый refresh-токен,
  //     либо прямой вызов onSessionExpired мимо эпизода — и то и другое
  //     требует человеческих критериев приёмки, которых по этой ревизии нет.
  // Окно закреплено тестом `tokenRefresh.web1848.test.ts` — если его решат
  // закрыть, тест обязан поменяться осознанно, а не «само стало зелёным».
  let endSessionOnFailure = false;

  async function doRefresh(): Promise<string> {
    const storedRefreshToken = deps.getStoredRefreshToken();
    if (!storedRefreshToken) {
      // Nothing to renew — this is either an anonymous visitor (never
      // logged in) or a legacy session that predates this feature (access
      // token in storage, no refresh token). Neither is a "session that
      // expired" from the user's point of view, so stay silent: no network
      // call, no forced-logout toast. The caller's original 401 propagates
      // exactly as it did before this feature existed.
      throw new Error('WEB-1114: no refresh token stored');
    }

    try {
      const result = await deps.postRefresh(storedRefreshToken);
      deps.onRefreshed(result);
      return result.accessToken;
    } catch (err) {
      // The refresh token was rejected — expired on its own, or the session
      // was revoked (logout / password change / password reset). Either
      // way there is no path back to a live session.
      // WEB-1848 — unless nobody in this episode claimed the right to end
      // the session (speculative EMAIL_NOT_VERIFIED refresh only): then the
      // caller just gets its rejection and the live session is left alone.
      // WEB-2613 — повисшее или оборванное обновление ничего не говорит о
      // сессии: выход только когда сервер ответил, что пропуск обновить нельзя.
      if (endSessionOnFailure && !deps.isTransientFailure?.(err)) {
        deps.onSessionExpired();
      }
      throw err;
    }
  }

  function refreshAccessToken(
    options?: RefreshAccessTokenOptions,
  ): Promise<string> {
    // Set BEFORE doRefresh() starts, and OR-ed across every caller that joins
    // the same in-flight episode.
    if (options?.endSessionOnFailure !== false) {
      endSessionOnFailure = true;
    }
    if (!inFlight) {
      // Single-flight: every caller that arrives before this settles shares
      // the SAME promise, so doRefresh() — and therefore postRefresh() and
      // onSessionExpired() — runs at most once per failed/succeeded episode.
      // The slot is cleared only once this attempt settles, so the *next*
      // 401 episode (e.g. after a fresh login) starts a brand-new refresh.
      inFlight = doRefresh().finally(() => {
        inFlight = null;
        // The next episode starts from a clean verdict — otherwise a single
        // 401 would grant every later speculative refresh the right to log
        // the user out.
        endSessionOnFailure = false;
      });
    }
    return inFlight;
  }

  return { refreshAccessToken };
}
